import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/" + hash);
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-lake-deep/85 backdrop-blur-xl border-b hairline-light"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 text-glacier">
          <img src="/guidone.png" alt="Guidone Circolo Vela Manerba del Garda" className="h-11 w-auto" data-testid="nav-logo-img" />
          <span className="font-serif text-lg sm:text-xl tracking-tight leading-none">
            Circolo Vela
            <span className="block text-[10px] font-mono uppercase tracking-[0.25em] text-glacier/60">
              Manerba del Garda
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-glacier/80">
          <button data-testid="nav-experiences-link" onClick={() => go("#esperienze")} className="hover:text-glacier transition-colors">
            {t.nav.experiences}
          </button>
          <button data-testid="nav-club-link" onClick={() => go("#circolo")} className="hover:text-glacier transition-colors">
            {t.nav.club}
          </button>
          <Link data-testid="admin-access-link" to="/admin/login" className="hover:text-glacier transition-colors text-glacier/50">
            {t.nav.admin}
          </Link>
          <button
            data-testid="language-toggle-btn"
            onClick={() => setLang(lang === "it" ? "en" : "it")}
            className="font-mono text-xs tracking-[0.2em] border border-glacier/25 rounded-full px-3 py-1.5 hover:bg-glacier/10 transition-colors"
          >
            {lang === "it" ? "EN" : "IT"}
          </button>
          <Link
            data-testid="start-booking-cta"
            to="/prenota"
            className="bg-gold text-lake-deep font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-glacier transition-colors duration-300"
          >
            {t.nav.book}
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-btn"
          className="md:hidden text-glacier"
          onClick={() => setOpen(!open)}
          aria-label="menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-lake-deep/95 backdrop-blur-xl border-t hairline-light px-6 py-6 flex flex-col gap-5 text-glacier">
          <button onClick={() => go("#esperienze")} className="text-left text-lg font-serif">{t.nav.experiences}</button>
          <button onClick={() => go("#circolo")} className="text-left text-lg font-serif">{t.nav.club}</button>
          <Link to="/admin/login" className="text-glacier/60">{t.nav.admin}</Link>
          <div className="flex items-center gap-4 pt-2">
            <button
              data-testid="language-toggle-btn-mobile"
              onClick={() => setLang(lang === "it" ? "en" : "it")}
              className="font-mono text-xs tracking-[0.2em] border border-glacier/25 rounded-full px-3 py-1.5"
            >
              {lang === "it" ? "EN" : "IT"}
            </button>
            <Link to="/prenota" className="bg-gold text-lake-deep font-semibold px-5 py-2.5 rounded-full text-sm">
              {t.nav.book}
            </Link>
          </div>
        </div>
      )}
    </motion.header>
  );
}
